import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { AccountType, ContextStatus, EProductType } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../auth/AuthenticationContext';
import createCompanyFromBackoffice from '../../../../domain/use_cases/companies/createCompanyFromBackoffice';
import PlansContext from '../PlansContext';
import { IPlan } from '@leedpulse-monorepo/interfaces';
import UserDoc from '../../../../domain/models/UserDoc';
import UsersContext from '../UsersContext';
import { useDebounce } from '@leedpulse-monorepo/hooks';

/** Form state shape aligned with Company model fields */

export interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  maxDigits: number;
}

export interface BusinessDomain {
  label: string;
  value: string;
}

// TODO : @MrWarzo centraliser
const COUNTRIES: Country[] = [
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', maxDigits: 9 },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪', maxDigits: 11 },
  { code: 'CH', name: 'Suisse', dialCode: '+41', flag: '🇨🇭', maxDigits: 9 },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧', maxDigits: 10 },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪', maxDigits: 9 },
];

const BUSINESS_DOMAINS = [{ label: 'Immobilier', value: 'domain-8F28730C7D' }];

/** Discriminated union: type depends on createNewUser value */
type UserPayload =
  | { createNewUser: true; firstname: string; lastname: string; email: string; phone: string }
  | { createNewUser: false; userDoc: { uid: string; email: string; phone: string } | null };

interface CompanyPayload {
  id: string;
  name: string;
  siret: string;
  notes: string;
  businessDomain: string;
  activityDescription: string;
}

interface PaymentPayload {
  plan: string;
  accountType: AccountType; // Renommer "paymentType" et adapter partout et changer pour "FREE" | "PAID"
}

interface StripePayload {
  id: string;
  createNewCustomer: boolean;
}

export interface CompanyCreationFormState {
  searchUsersQuery: string;
  searchUsersResults: UserDoc[];
  searchUsersLoading: boolean;
  // Form Data
  user: UserPayload;
  company: CompanyPayload;
  payment: PaymentPayload;
  stripe: StripePayload;
}

const defaultFormState: CompanyCreationFormState = {
  searchUsersQuery: '',
  searchUsersResults: [],
  searchUsersLoading: false,
  user: {
    createNewUser: true,
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
  },
  company: {
    id: '',
    name: '',
    siret: '',
    notes: '',
    businessDomain: '',
    activityDescription: '',
  },
  payment: {
    plan: '',
    accountType: AccountType.COMPANY, // TODO : Remplacer par "FREE" | "PAID"
  },
  stripe: {
    id: '',
    createNewCustomer: true,
  },
};

const CompanyCreationFormContext = createContext({
  status: ContextStatus.IDLE,
  ...defaultFormState,
  setSearchUsersQuery: (_: string) => { return; },
  // user changes
  onFirstnameChange: (_: string) => { return; },
  onLastnameChange: (_: string) => { return; },
  onEmailChange: (_: string) => { return; },
  onPhoneChange: (_: string) => { return; },
  onCreateNewUserChange: (_: boolean) => { return; },
  onUserDocChange: (_: { uid: string; email: string; phone: string } | null) => { return; },
  // company changes
  onNameChange: (_: string) => { return; },
  onSiretChange: (_: string) => { return; },
  onNotesChange: (_: string) => { return; },
  onBusinessDomainChange: (_: string) => { return; },
  onActivityDescriptionChange: (_: string) => { return; },
  // payment changes
  onAccountTypeChange: (_: AccountType) => { return; },
  onPlanChange: (_: string) => { return; },
  // stripe changes
  onIdStripeChange: (_: string) => { return; },
  onCreateNewCustomerChange: (_: boolean) => { return; },
  submit: () => { return; },
  // consts
  countries: COUNTRIES as Country[],
  businessDomains: BUSINESS_DOMAINS as BusinessDomain[],
  plans: { monthly: [], annual: [] } as { monthly: IPlan[], annual: IPlan[] },
});

export const CompanyCreationFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const authContext = useContext(AuthenticationContext);
  const plansContext = useContext(PlansContext);
  const usersContext = useContext(UsersContext);

  const [searchUsersResults, setSearchUsersResults] = useState<UserDoc[]>(defaultFormState.searchUsersResults);
  const [searchUsersLoading, setSearchUsersLoading] = useState(defaultFormState.searchUsersLoading);
  const searchUsersQueryRef = useRef('');
  const [searchUsersQuery, setSearchUsersQueryState] = useDebounce('', 500, () => {
    const query = searchUsersQueryRef.current.trim();
    if (!query) {
      setSearchUsersResults([]);
      setSearchUsersLoading(false);
      return;
    }
    setSearchUsersLoading(true);
    void usersContext.search(query, true).then((results) => {
      setSearchUsersResults(results);
      setSearchUsersLoading(false);
    });
  });
  const setSearchUsersQuery = (value: string) => {
    searchUsersQueryRef.current = value;
    setSearchUsersQueryState(value);
    if (!value.trim()) {
      setSearchUsersLoading(false);
    }
  };

  // Form data
  const [user, setUser] = useState<UserPayload>(defaultFormState.user);
  const [company, setCompany] = useState<CompanyPayload>(defaultFormState.company);
  const [payment, setPayment] = useState<PaymentPayload>(defaultFormState.payment);
  const [stripe, setStripe] = useState<StripePayload>(defaultFormState.stripe);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  // States
  const [plans, setPlans] = useState<{ monthly: IPlan[], annual: IPlan[] }>({ monthly: [], annual: [] });

  // User methods (only apply when createNewUser is true)
  const onFirstnameChange = (value: string) => {
    if (user.createNewUser) setUser(o => ({ ...o, firstname: value }));
  };
  const onLastnameChange = (value: string) => {
    if (user.createNewUser) setUser(o => ({ ...o, lastname: value }));
  };
  const onEmailChange = (value: string) => {
    if (user.createNewUser) setUser(o => ({ ...o, email: value }));
  };
  const onPhoneChange = (value: string) => {
    if (user.createNewUser) setUser(o => ({ ...o, phone: value }));
  };
  const onCreateNewUserChange = (value: boolean) => {
    if (value) {
      setUser({
        createNewUser: true,
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
      });
    } else {
      setUser({ createNewUser: false, userDoc: null });
    }
  };
  const onUserDocChange = (value: { uid: string; email: string; phone: string } | null) => {
    if(!user.createNewUser) setUser(o => ({ ...o, userDoc: value }));
  };

  // Company methods
  const onNameChange = (value: string) => setCompany(o => ({ ...o, name: value }));
  const onSiretChange = (value: string) => setCompany(o => ({ ...o, siret: value }));
  const onNotesChange = (value: string) => setCompany(o => ({ ...o, notes: value }));
  const onBusinessDomainChange = (value: string) => setCompany(o => ({ ...o, businessDomain: value }));
  const onActivityDescriptionChange = (value: string) => setCompany(o => ({ ...o, activityDescription: value }));
  
  // Payment methods - when switching to free, clear plan and reset stripe; when switching to paid, reset stripe id
  const onAccountTypeChange = (value: AccountType) => {
    setPayment(o => ({ ...o, accountType: value, plan: value === AccountType.SUBSCRIPTION ? '' : payment.plan }));
    if (value === AccountType.COMPANY) {
      setStripe(o => ({ ...o, createNewCustomer: true, id: '' }));
    } else {
      setStripe(o => ({ ...o, id: '' }));
    }
  };
  const onPlanChange = (value: string) => setPayment(o => ({ ...o, plan: value }));
  
  // Stripe methods
  const onIdStripeChange = (value: string) => setStripe(o => ({ ...o, id: value }));
  const onCreateNewCustomerChange = (value: boolean) => setStripe(o => ({ ...o, createNewCustomer: value }));

  const submit = async () => {
    const isUserValid =
      user.createNewUser
        ? user.firstname !== '' && user.lastname !== '' && user.email !== '' && user.phone !== ''
        : !!user.userDoc;

    const hasPlan = payment.accountType === AccountType.SUBSCRIPTION && payment.plan !== '';
    const needsStripeId = hasPlan || (!stripe.createNewCustomer && stripe.id.trim() !== '');

    const isStripeValid =
      hasPlan
        ? stripe.id.trim() !== ''
        : stripe.createNewCustomer || stripe.id.trim() !== '';

    if (
      authContext.authentication === null ||
      !isUserValid ||
      company.name.trim() === '' ||
      !isStripeValid ||
      (needsStripeId && stripe.id.trim() === '')
    ) {
      return;
    }

    if (!user.createNewUser && !user.userDoc) return;

    setStatus(ContextStatus.LOADING);
    try {
      await createCompanyFromBackoffice(
        {
          user,
          company: { name: company.name.trim(), siret: company.siret?.trim() || '', notes: company.notes || '' },
          payment,
          stripe,
        },
        authContext.authentication
      );
      setStatus(ContextStatus.SUCCESS);
    } catch {
      setStatus(ContextStatus.ERROR);
    }
  };

  useEffect(() => {
    plansContext.fetch({ product: EProductType.LEEDFLOW }).then(() => {
      const monthly: IPlan[] = [];
      const annual: IPlan[] = [];

      plansContext.items.filter((plan) => !plan.archived).forEach((plan) => plan.id.includes('monthly') ? monthly.push(plan) : annual.push(plan));

      setPlans({ monthly: monthly.sort((a, b) => a.price?.value - b.price?.value), annual: annual.sort((a, b) => a.price?.value - b.price?.value) });
    });
  }, []);

  return (
    <CompanyCreationFormContext.Provider
      value={{
        status,
        searchUsersQuery,
        setSearchUsersQuery,
        searchUsersResults,
        searchUsersLoading,
        // form data
        user,
        company,
        payment,
        stripe,
        // user changes
        onFirstnameChange,
        onLastnameChange,
        onEmailChange,
        onPhoneChange,
        onCreateNewUserChange,
        onUserDocChange,
        // company changes
        onNameChange,
        onSiretChange,
        onNotesChange,
        onBusinessDomainChange,
        onActivityDescriptionChange,
        // payment changes
        onAccountTypeChange,
        onPlanChange,
        // stripe changes
        onIdStripeChange,
        onCreateNewCustomerChange,
        // funcs
        submit,
        // consts
        countries: COUNTRIES,
        businessDomains: BUSINESS_DOMAINS,
        plans: plans,
      }}
    >
      {children}
    </CompanyCreationFormContext.Provider>
  );
};

export default CompanyCreationFormContext;
